<template>
    <div v-if="bare">
        <div class="tw:border-b tw:border-gray-200">
            <nav class="tw:flex tw:gap-3 tw:-mb-px tw:overflow-x-auto tabs-nav">
                <button
                    v-for="tab in visibleTabs"
                    :key="tab.key"
                    type="button"
                    class="tw:px-4 tw:py-3 tw:text-sm tw:whitespace-nowrap tw:border-b-2 tw:transition-colors tw:duration-150"
                    :class="activeTab === tab.key
                        ? 'tw:border-blue-600 tw:text-blue-600  tw:font-bold'
                        : 'tw:border-transparent tw:text-gray-400 tw:hover:text-gray-700'"
                    @click="handleTabClick(tab.key)"
                >
                    {{ tab.label }}
                </button>
            </nav>
        </div>

        <div :class="contentClass">
            <template v-for="tab in visibleTabs">
                <div v-if="visitedTabs[tab.key]" v-show="activeTab === tab.key" :key="tab.key">
                    <slot :name="'tab-' + tab.key"></slot>
                </div>
            </template>
        </div>
    </div>
    <Card v-else :padding="0">
        <div class="tw:border-b tw:border-gray-200">
            <nav class="tw:flex tw:gap-3 tw:-mb-px tw:overflow-x-auto tabs-nav">
                <button
                    v-for="tab in visibleTabs"
                    :key="tab.key"
                    type="button"
                    class="tw:px-4 tw:py-3 tw:text-sm tw:whitespace-nowrap tw:border-b-2 tw:transition-colors tw:duration-150"
                    :class="activeTab === tab.key
                        ? 'tw:border-blue-600 tw:text-blue-600  tw:font-bold'
                        : 'tw:border-transparent tw:text-gray-400 tw:hover:text-gray-700'"
                    @click="handleTabClick(tab.key)"
                >
                    {{ tab.label }}
                </button>
            </nav>
        </div>

        <div :class="contentClass">
            <template v-for="tab in visibleTabs">
                <div v-if="visitedTabs[tab.key]" v-show="activeTab === tab.key" :key="tab.key">
                    <slot :name="'tab-' + tab.key"></slot>
                </div>
            </template>
        </div>
    </Card>
</template>

<script setup>
    import { ref, computed, watch } from 'vue';
    import Card from '@/modules/admin/ui/Card.vue'

    const props = defineProps({
        tabs: {
            type: Array,
            required: true
        },
        initialTab: {
            type: String,
            default: null
        },
        /**
         * Render the panel without the Card wrapper. Use when the tabs
         * live inside a host that already provides its own framing
         * (e.g. a modal with its own header). The content area still
         * gets the same `contentClass` padding.
         */
        bare: {
            type: Boolean,
            default: false
        },
        /**
         * Padding class applied to the tab content area. The bare
         * variant of TabsPanel exposes this so callers can space
         * the panel interior to taste.
         */
        contentClass: {
            type: String,
            default: ''
        }
    })

    const activeTab = ref(null)
    // Track visited tabs using plain object for Vue reactivity.
    // Prevents component remounting when switching tabs.
    const visitedTabs = ref({})
    // Track if initialTab requirement has been satisfied
    const initialTabSatisfied = ref(false)

    const visibleTabs = computed(() => props.tabs.filter(t => t.show !== false))

    function handleTabClick(tabKey) {
        // Update visitedTabs with spread operator to trigger reactivity
        visitedTabs.value = { ...visitedTabs.value, [tabKey]: true }
        activeTab.value = tabKey
        initialTabSatisfied.value = true
        emit('tab-change', tabKey)
    }

    const emit = defineEmits(['tab-change'])

    watch(() => visibleTabs.value, newVisibleTabs => {
        // Priority 1: Satisfy initialTab if it's now visible and we haven't yet
        if (props.initialTab && !initialTabSatisfied.value && newVisibleTabs.some(t => t.key === props.initialTab)) {
            activeTab.value = props.initialTab
            visitedTabs.value = { ...visitedTabs.value, [props.initialTab]: true }
            initialTabSatisfied.value = true
            return
        }
        
        // Priority 2: Fallback if active tab is now hidden or missing
        if (!activeTab.value || !newVisibleTabs.some(t => t.key === activeTab.value)) {
            const firstKey = newVisibleTabs[0]?.key
            if (firstKey) {
                activeTab.value = firstKey
                visitedTabs.value = { ...visitedTabs.value, [firstKey]: true }
            }
        }
    }, { immediate: true })
</script>

<style scoped>
</style>
